/**
 * Chart factory for creating chart instances
 */
import VChart from '@visactor/vchart';
import { LIGHT_THEME, DARK_THEME } from '../config/themes';
import { MAX_CHART_DATA_POINTS } from './limits';

// Register necessary chart types
import {
  registerBarChart,
  registerPieChart,
  registerLineChart,
  registerAreaChart,
  registerScatterChart,
  registerRadarChart,
  registerRoseChart,
  registerBoxplotChart,
  registerFunnelChart,
  registerWordCloudChart,
  registerHeatmapChart,
  registerMapChart,
  registerTreemapChart,
  registerSankeyChart,
  registerSunburstChart,
  registerGaugeChart,
  registerRangeColumnChart,
  registerLinearProgressChart,
  registerCircularProgressChart,
  registerWaterfallChart,
  registerCommonChart
} from '@visactor/vchart';

// Import chart implementations
import BarChart from './BarChart';
import PieChart from './PieChart';
import LineChart from './LineChart';
import AreaChart from './AreaChart';
import ScatterChart from './ScatterChart';
import RadarChart from './RadarChart';
import RoseChart from './RoseChart';
import BoxPlotChart from './BoxPlotChart';
import FunnelChart from './FunnelChart';
import WordCloudChart from './WordCloudChart';
import HeatmapChart from './HeatmapChart';
import MapChart from './MapChart';
import TreemapChart from './TreemapChart';
import SankeyChart from './SankeyChart';
import SunburstChart from './SunburstChart';
import GaugeChart from './GaugeChart';
import RangeChart from './RangeChart';
import ProgressChart from './ProgressChart';
import WaterfallChart from './WaterfallChart';
import CompositeChart from './CompositeChart';

// Register all chart types with VChart
// eslint-disable-next-line react-hooks/rules-of-hooks
VChart.useRegisters([
  registerBarChart,
  registerPieChart,
  registerLineChart,
  registerAreaChart,
  registerScatterChart,
  registerRadarChart,
  registerRoseChart,
  registerBoxplotChart,
  registerFunnelChart,
  registerWordCloudChart,
  registerHeatmapChart,
  registerMapChart,
  registerTreemapChart,
  registerSankeyChart,
  registerSunburstChart,
  registerGaugeChart,
  registerRangeColumnChart,
  registerLinearProgressChart,
  registerCircularProgressChart,
  registerWaterfallChart,
  registerCommonChart
]);

// Chart type mapping
const CHART_TYPES = {
  bar: BarChart,
  pie: PieChart,
  line: LineChart,
  area: AreaChart,
  scatter: ScatterChart,
  radar: RadarChart,
  rose: RoseChart,
  boxplot: BoxPlotChart,
  funnel: FunnelChart,
  wordcloud: WordCloudChart,
  heatmap: HeatmapChart,
  map: MapChart,
  treemap: TreemapChart,
  sankey: SankeyChart,
  sunburst: SunburstChart,
  gauge: GaugeChart,
  range: RangeChart,
  progress: ProgressChart,
  waterfall: WaterfallChart,
  composite: CompositeChart,
};

/**
 * Create a chart instance from a validated chart spec.
 * @param {Object} chartData Validated chart spec with chart_type, data, title?, options?
 * @param {HTMLElement} container DOM container for the chart
 * @param {Object} factoryOptions Factory options including isDarkMode
 * @returns {Object} Chart instance
 */
export function createChart(chartData, container, factoryOptions = {}) {
  const { isDarkMode = false } = factoryOptions;

  if (!chartData || !Array.isArray(chartData.data)) {
    throw new Error('Invalid chart spec: requires data array');
  }

  if (!chartData.chart_type) {
    console.warn('[ChartFactory] No chart_type provided, defaulting to bar');
    chartData = { ...chartData, chart_type: 'bar' };
  }

  if (chartData.data.length > MAX_CHART_DATA_POINTS) {
    throw new Error(`Chart data exceeds ${MAX_CHART_DATA_POINTS} point limit`);
  }

  const ChartClass = CHART_TYPES[chartData.chart_type];
  if (!ChartClass) {
    throw new Error(`Unsupported chart type: ${chartData.chart_type}`);
  }

  const options = { ...(chartData.options || {}) };
  if (chartData.title) {
    options.title = { display: true, text: chartData.title };
  }
  options.theme = isDarkMode ? DARK_THEME : LIGHT_THEME;

  const chart = new ChartClass(chartData.data, options, container);
  chart.render();
  return chart;
}

export default { createChart };